import React, { useEffect, useState } from 'react';
import { ChevronDown, Flag } from 'lucide-react';
import { listGameResults } from '../lib/api';
import { opponentShortName, TextFinalScoreButton } from './GameResultBadge';
import { GamePhotoStrip } from './GamePhotoStrip';
import { SeasonStandingsCard } from './SeasonStandingsCard';

const formatDate = (date) => new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const OUTCOME_TAG = { win: ['W', 'bg-emerald-700'], loss: ['L', 'bg-[#0a1f44]'], tie: ['T', 'bg-[#d97706]'] };

export const GameResultsArchive = () => {
  const [games, setGames] = useState([]);
  const [status, setStatus] = useState('loading');
  const [open, setOpen] = useState(() => window.location.hash === '#game-results');

  useEffect(() => {
    listGameResults().then((data) => { setGames(data); setStatus('ready'); }).catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    if (status !== 'ready' || window.location.hash !== '#game-results') return;
    const target = document.getElementById('game-results');
    if (target) window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
  }, [status]);

  if (status !== 'ready' || games.length === 0) return null;
  const wins = games.filter((g) => g.result.outcome === 'win').length;
  const losses = games.filter((g) => g.result.outcome === 'loss').length;
  const ties = games.length - wins - losses;

  return (
    <section id="game-results" className="mt-10 border-t-2 border-[#0a1f44] pt-6" aria-labelledby="game-results-archive-heading" data-testid="game-results-archive">
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="game-results-archive-panel" data-testid="game-results-archive-toggle" className="w-full flex items-center justify-between gap-4 text-left group">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#c8102e] text-white group-hover:bg-[#0a1f44] transition-colors"><Flag className="w-5 h-5" /></div>
          <div><div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Season Scoreboard</div><h2 id="game-results-archive-heading" className="font-display text-3xl sm:text-4xl text-[#0a1f44] mt-1">GAME RESULTS</h2></div>
        </div>
        <div className="flex items-center gap-2 text-[#0a1f44]/60 font-heading uppercase tracking-widest text-[10px]"><span data-testid="game-results-record">{wins}-{losses}{ties ? `-${ties}` : ''}</span><ChevronDown className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} /></div>
      </button>
      {open && (
        <div id="game-results-archive-panel" className="mt-6 space-y-4" data-testid="game-results-archive-panel">
          <SeasonStandingsCard games={games} />
          <div className="divide-y divide-[#0a1f44]/10 border border-[#0a1f44]/15 bg-white">
          {games.map((game) => {
            const [tag, color] = OUTCOME_TAG[game.result.outcome] || OUTCOME_TAG.tie;
            return (
              <article key={game.id} data-testid={`game-results-row-${game.id}`} className="p-4 sm:p-5 grid grid-cols-[auto_1fr] sm:grid-cols-[auto_140px_1fr_auto] gap-x-4 gap-y-2 items-center">
                <div className={`w-9 h-9 flex items-center justify-center font-display text-lg text-white ${color}`} aria-label={game.result.outcome}>{tag}</div>
                <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60">{formatDate(game.date)}</div>
                <div className="col-span-2 sm:col-span-1 min-w-0">
                  <div className="font-display text-xl sm:text-2xl text-[#0a1f44] tabular-nums" data-testid={`game-results-score-${game.id}`}>BJP {game.result.patriotsScore} – {opponentShortName(game)} {game.result.opponentScore}</div>
                  <div className="text-xs text-[#0a1f44]/60 truncate">{game.opponent || 'Opponent'} · {game.location}{game.result.recap ? ` · ${game.result.recap}` : ''}</div>
                  <GamePhotoStrip game={game} compact />
                </div>
                <TextFinalScoreButton game={game} className="col-span-2 sm:col-span-1 border-[#c8102e] text-[#c8102e] hover:bg-[#c8102e] hover:text-white" />
              </article>
            );
          })}
          </div>
        </div>
      )}
    </section>
  );
};
